import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import Rating from '@mui/material/Rating'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppSportsReviewDraft,
    clearAppSportsReviewDraft,
    createAppSportsReviewDraftPayload,
    createEditAppSportsReviewDraftKey,
    readAppSportsReviewDraft,
    saveAppSportsReviewDraft,
} from './appSportsReviewDraftUtils'

export default function AppSportsReview(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.sportsReviewId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: 99, searchPopupStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditAppSportsReviewDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState({})
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 App 운동시설 리뷰 상세를 불러올 수 없습니다.'
                setParams({})
                setHasDetail(false)
                setDraftReady(false)
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appSportsReview/getDetail', { sportsReviewId: id })
                // console.log('detail: ', data)
                if (data.code === 200) {
                    const baseParams = data.data || {}

                    if (!baseParams.sportsReviewId) {
                        setParams({})
                        setDetailErrorMessage('App 운동시설 리뷰 상세 정보를 찾을 수 없습니다.')
                        setLoading(false)
                        return
                    }

                    const storedDraft = readAppSportsReviewDraft(draftKey)
                    const restoredDraft = applyAppSportsReviewDraft({
                        baseParams,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppSportsReviewDraftPayload({
                        params: baseParams,
                    }))

                    setParams(restoredDraft.params)
                    setHasDetail(true)
                    setDetailErrorMessage('')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'App 운동시설 리뷰 상세를 불러오지 못했습니다.'
                    setParams({})
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'App 운동시설 리뷰 상세를 불러오지 못했습니다.'
                setParams({})
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }

            setLoading(false)
        }
        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !params?.sportsReviewId) {
            return
        }

        // App 운동시설 리뷰 수정은 목록 query를 건드리지 않고 별점/내용 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppSportsReviewDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppSportsReviewDraft(draftKey)
            return
        }

        saveAppSportsReviewDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        // 저장 후에도 운영자가 보던 App 운동시설 리뷰 목록 조건으로 그대로 복귀시킨다.
        clearAppSportsReviewDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: `/appSportsReviews`,
            search: listSearch,
            openSnackbar: snackbarState?.openSnackbar,
            severitySnackbar: snackbarState?.severitySnackbar,
            messageSnackbar: snackbarState?.messageSnackbar,
            curPage: curPage,
            searchText: searchText,
            searchSelect: searchSelect,
        }))
    }

    const handleUpdateField = (e) => {
        if (e.target.name === 'star') {
            setParams({
                ...params,
                [e.target.name]: Number(e.target.value)
            })
        } else {
            setParams({
                ...params,
                [e.target.name]: e.target.value
            })
        }
    }

    const updateData = async () => {
        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appSportsReview/update2', { sportsReviewId: id, star: params.star, content: params.content})

            if (data.code === 200) {
                moveToList({
                    openSnackbar: true,
                    severitySnackbar: 'success',
                    messageSnackbar: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('운동시설 리뷰를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        updateData()
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 운동시설 리뷰 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appSportsReviews" search={listSearch} visible={false} curPage={curPage} searchText={searchText} searchSelect={searchSelect} onBackClick={() => clearAppSportsReviewDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 운동시설 리뷰 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 운동시설 리뷰 수정</Title>
                <Box component="form" onSubmit={handleSubmit} >
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={4} md={4} sm={4}>
                            <FormControl fullWidth>
                                <TextField
                                    label="시설명"
                                    id="title"
                                    name="title"
                                    value={params.app_sports.title || ''}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={8} md={8} sm={8}>
                            <FormControl fullWidth>
                                <TextField
                                    label="주소"
                                    id="address"
                                    name="address"
                                    value={params.app_sports.address || ''}
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl component="fieldset">
                                <Typography component="legend">별점</Typography>
                                <Rating
                                    id="star"
                                    name="star"
                                    value={params.star || 1}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <FormControl fullWidth>
                                <TextField
                                    label="내용"
                                    id="content"
                                    name="content"
                                    multiline
                                    rows={15}
                                    value={params.content || ''}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        path="/appSportsReviews"
                        search={listSearch}
                        visible={true}
                        curPage={curPage}
                        searchText={searchText}
                        searchSelect={searchSelect}
                        onBackClick={() => clearAppSportsReviewDraft(draftKey)}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
